/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IRequestContext } from '@msdyn365-commerce/core';
import { debounce } from 'lodash';
import { observer } from 'mobx-react';
import * as React from 'react';

import { isMobile, VariantType } from '@msdyn365-commerce-modules/utilities';
import { getPayloadObject, getTelemetryAttributes, IPayLoad, TelemetryConstant } from '@msdyn365-commerce-modules/utilities/';
import { IRwbIncrementalQuantityProps } from './RwbIncrementalQuantity.props';

/**
 * IIncrementalQuantityState interface.
 */
interface IIncrementalQuantityState {
    currentInput: number;
    isUpdating: boolean;
}

/**
 * Quantity Component - This component is used to add or remove quantity.
 */
@observer
export default class RwbIncrementalQuantity extends React.PureComponent<IRwbIncrementalQuantityProps, IIncrementalQuantityState> {
    public static defaultProps: Partial<IRwbIncrementalQuantityProps> = {
        min: 1,
        decrementGlyphClass: 'fas fa-minus',
        incrementGlyphClass: 'fas fa-plus'
    };

    private _minValue: number;
    private converage = 1;
    private nst = 1;

    private conversion = 1;
    private readonly inputRef: React.RefObject<HTMLInputElement> = React.createRef<HTMLInputElement>();

    private readonly payLoad: IPayLoad;

    public static getDerivedStateFromProps(
        props: Readonly<IRwbIncrementalQuantityProps>,
        state: IIncrementalQuantityState
    ): IIncrementalQuantityState {
        const nextState = { ...state };

        if (props.disabled && !state.isUpdating) {
            nextState.isUpdating = true;
        }

        if (
            (!props.disabled && state.isUpdating) ||
            ((props.applyDefaultOrderSettings ?? false) &&
                state.currentInput === 1 &&
                props.currentCount &&
                props.currentCount > RwbIncrementalQuantity.defaultProps.min!)
        ) {
            nextState.currentInput = props.currentCount ?? 1;
            nextState.isUpdating = false;
        }

        return nextState;
    }

    constructor(props: IRwbIncrementalQuantityProps) {
        super(props);
        this.state = { currentInput: props.currentCount ?? 1, isUpdating: false };

        const parts = props.UOMCollection ? props.UOMCollection.split('|') : [];
        parts.forEach(element => {
            const foundNST = element.match(/(\1\sNST\s=\s)(\d+\.*\d*)\sSQFT/);
            if (foundNST) {
                this.conversion = Number.parseFloat(foundNST[2]);
                this.converage = this.converage * this.conversion;

            }
            const foundCTN = element.match(/(\1\sCTN\s=\s)(\d+\.*\d*)\sSQFT/);
            if (foundCTN) {
                this.conversion = Number.parseFloat(foundCTN[2]);
                this.converage = this.converage * this.conversion;
            }
        });

        this._onIncrement = this._onIncrement.bind(this);
        this._onDecrement = this._onDecrement.bind(this);
        this._handleChange = this._handleChange.bind(this);
        this._handleCoverageChange = this._handleCoverageChange.bind(this);
        this._handleNstChange = this._handleNstChange.bind(this);
        this.payLoad = getPayloadObject('click', this.props.telemetryContent!, '');
        this._minValue = this.props.min ?? RwbIncrementalQuantity.defaultProps.min!;
        if (this.props.UOMCollection !== undefined) {
            var value = this.state.currentInput;
            if (value < this.nst * this.conversion) {
                value = this.toTwoDecimal(this.nst * this.conversion);
                if (this.props.onChange) {
                    this.props.onChange(value);
                }
            } else {
                this.nst = Math.round(value / this.conversion);
            }
        }
    }

    public render(): JSX.Element {
        const { min, max } = this.props;

        this._minValue = min ?? RwbIncrementalQuantity.defaultProps.min!;
        if (this.props.currentCount !== undefined && this.props.currentCount !== this.state.currentInput && this.state.currentInput ===1) {
            this.setState({ currentInput: this.props.currentCount??1  }); // to re render when min prop changes
        }
        var value = this.state.currentInput;

        const glyphMinusClassName: string = `${this.props.decrementGlyphClass!} quantity__controls-glyph`;
        const glyphPlusClassName: string = `${this.props.incrementGlyphClass!} quantity__controls-glyph`;
        const decrementDisabled = value <= this._minValue || this.props.isGiftCard;
        const incrementDisabled = value >= max || this.props.isGiftCard;
        const currentValue = value;
        this.payLoad.contentAction.etext = TelemetryConstant.DecrementQuantity;
        const decrementAttributes = getTelemetryAttributes(this.props.telemetryContent!, this.payLoad);
        this.payLoad.contentAction.etext = TelemetryConstant.IncrementQuantity;
        const incrementAttributes = getTelemetryAttributes(this.props.telemetryContent!, this.payLoad);
        const ariaLablelText = `${this.props.inputQuantityAriaLabel ?? ''} ${value}`;

        const price = this.props.price ? this.props.price : 0;
        let extraClassDecrement = '';
        if (decrementDisabled) {
            // The quantity has reached its boundaries (max or min)
            extraClassDecrement = 'disabled';
        } else if (this.props.disabled) {
            // This.props.disabled shows if the state is not succeded yet
            extraClassDecrement = 'transition';
        }
        let extraClassIncrement = '';
        if (incrementDisabled) {
            // The quantity has reached its boundaries (max or min)
            extraClassIncrement = 'disabled';
        } else if (this.props.disabled) {
            // This.props.disabled shows if the state is not succeded yet
            extraClassIncrement = 'transition';
        }

        var disabled = this.props.disabled;
        if (this.props.UOMCollection !== undefined) {
            if (value < this.nst * this.conversion) {
                value = this.toTwoDecimal(this.nst * this.conversion);
            } else {
                this.nst = Math.round(value / this.conversion);
            }
            return (
                <>
                    {this.props.caller !== 'cartlineitem' &&
                        <div>
                            <div className='rwb-buybox-calc-title'>{this.props.calcTitle}</div>
                            <table className='rwb-buybox-calc-table'>
                                <thead className='rwb-buybox-calc-table-thead'></thead>
                                <tbody className='rwb-buybox-calc-table-tbody'>
                                    <tr>
                                        <td className='rwb-buybox-calc-table-td-sqft'>
                                            <input
                                                type="number"
                                                name="coverage"
                                                onChange={this._handleCoverageChange}
                                                value={this.converage}
                                                min={this._minValue}
                                                max={max}
                                                className='rwb-buybox-calc-table-input-sqft'
                                            />
                                            <div className='rwb-buybox-cacl-sqft'>SQFT</div>
                                            <div className='rwb-buybox-calc-total'>{this.nst} Bundle = {(this.nst * this.conversion).toFixed(2)} SQFT </div>
                                            <div className='rwb-buybox-calc-total_pirce'>${(this.nst * this.conversion * price).toFixed(2)}</div>
                                        </td>
                                        <td className='rwb-buybox-calc-table-td-mid'>
                                            OR
                                        </td>
                                        <td className='rwb-buybox-calc-table-td-nst'>
                                            <input
                                                type="number"
                                                name="nst"
                                                onChange={this._handleNstChange}
                                                value={this.nst}
                                                min={this._minValue}
                                                max={max}
                                                className='rwb-buybox-calc-table-input-nst'
                                            />
                                            <div className='rwb-buybox-cacl-bundles'>Bundles</div>
                                            <div className='rwb-buybox-calc-price'>${(price * this.conversion).toFixed(2)}/Bundle</div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>}
                    <div className="quantity" id={this.props.id}>
                        <button
                            disabled={disabled || decrementDisabled}
                            title={decrementDisabled ? '' : this.props.decrementButtonAriaLabel}
                            className={`decrement quantity__controls ${extraClassDecrement}`}
                            onClick={this._onDecrement}
                            aria-hidden={!!decrementDisabled}
                            aria-label={`${this.props.decrementButtonAriaLabel}`}
                            color="secondary"
                            {...decrementAttributes}
                        >
                            <span className={glyphMinusClassName} />
                        </button>
                        <input
                            type="number"
                            className="quantity-input"
                            pattern="[0-9]*"
                            value={value}
                            readOnly={false}
                            onChange={this._handleChange}
                            onBlur={this._validateMin}
                            aria-live="polite"
                            aria-label={this.isMobileView() ? ariaLablelText : `${this.props.inputQuantityAriaLabel ?? ''}`}
                            role="spinbutton"
                            aria-valuemin={this._minValue}
                            aria-valuemax={max}
                            aria-valuenow={currentValue}
                            ref={this.inputRef}
                            disabled={disabled}
                            min={this._minValue}
                            max={max}
                        />
                        <button
                            disabled={disabled || incrementDisabled}
                            title={incrementDisabled ? '' : this.props.incrementButtonAriaLabel}
                            className={`increment quantity__controls ${extraClassIncrement}`}
                            onClick={this._onIncrement}
                            aria-hidden={!!incrementDisabled}
                            aria-label={`${this.props.incrementButtonAriaLabel}`}
                            color="secondary"
                            {...incrementAttributes}
                        >
                            <span className={glyphPlusClassName} />
                        </button>
                    </div>
                </>
            );
        } else if (this.props.multipleOfQuantity &&this.props.multipleOfQuantity !== 0) {
            return (
                <>
                    <div className="quantity" id={this.props.id}>
                        <button
                            disabled={this.props.disabled || decrementDisabled}
                            title={decrementDisabled ? '' : this.props.decrementButtonAriaLabel}
                            className={`decrement quantity__controls ${extraClassDecrement}`}
                            onClick={this._onDecrement}
                            aria-hidden={!!decrementDisabled}
                            aria-label={`${this.props.decrementButtonAriaLabel}`}
                            color="secondary"
                            {...decrementAttributes}
                        >
                            <span className={glyphMinusClassName} />
                        </button>
                        <input
                            type="number"
                            className="quantity-input"
                            pattern="[0-9]*"
                            value={value}
                            readOnly={false}
                            onChange={this._handleChange}
                            onBlur={this._validateMin}
                            aria-live="polite"
                            aria-label={this.isMobileView() ? ariaLablelText : `${this.props.inputQuantityAriaLabel ?? ''}`}
                            role="spinbutton"
                            aria-valuemin={this._minValue}
                            aria-valuemax={max}
                            aria-valuenow={currentValue}
                            ref={this.inputRef}
                            disabled={this.props.disabled}
                            min={this._minValue}
                            max={max}
                        />
                        <button
                            disabled={this.props.disabled || incrementDisabled}
                            title={incrementDisabled ? '' : this.props.incrementButtonAriaLabel}
                            className={`increment quantity__controls ${extraClassIncrement}`}
                            onClick={this._onIncrement}
                            aria-hidden={!!incrementDisabled}
                            aria-label={`${this.props.incrementButtonAriaLabel}`}
                            color="secondary"
                            {...incrementAttributes}
                        >
                            <span className={glyphPlusClassName} />
                        </button>
                    </div>
                </>
            );
        } else {
            return (
                <>
                    <div className="quantity" id={this.props.id}>
                        <button
                            disabled={this.props.disabled || decrementDisabled}
                            title={decrementDisabled ? '' : this.props.decrementButtonAriaLabel}
                            className={`decrement quantity__controls ${extraClassDecrement}`}
                            onClick={this._onDecrement}
                            aria-hidden={!!decrementDisabled}
                            aria-label={`${this.props.decrementButtonAriaLabel}`}
                            color="secondary"
                            {...decrementAttributes}
                        >
                            <span className={glyphMinusClassName} />
                        </button>
                        <input
                            type="number"
                            className="quantity-input"
                            pattern="[0-9]*"
                            value={value}
                            onChange={this._handleChange}
                            onBlur={this._validateMin}
                            aria-live="polite"
                            aria-label={this.isMobileView() ? ariaLablelText : `${this.props.inputQuantityAriaLabel ?? ''}`}
                            role="spinbutton"
                            aria-valuemin={this._minValue}
                            aria-valuemax={max}
                            aria-valuenow={currentValue}
                            ref={this.inputRef}
                            disabled={this.props.disabled}
                            min={this._minValue}
                            max={max}
                        />
                        <button
                            disabled={this.props.disabled || incrementDisabled}
                            title={incrementDisabled ? '' : this.props.incrementButtonAriaLabel}
                            className={`increment quantity__controls ${extraClassIncrement}`}
                            onClick={this._onIncrement}
                            aria-hidden={!!incrementDisabled}
                            aria-label={`${this.props.incrementButtonAriaLabel}`}
                            color="secondary"
                            {...incrementAttributes}
                        >
                            <span className={glyphPlusClassName} />
                        </button>
                    </div>
                </>
            );
        }
    }

    /**
     * It checks if its in mobile view or not.
     * @returns Boolean.
     */
    private readonly isMobileView = (): boolean => {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Added gridsetting values.
        const context = {
            gridSettings: {
                xs: { w: 767 },
                sm: { w: 991 },
                md: { w: 1199 },
                lg: { w: 1599 },
                xl: { w: 1600 }
            }
        } as IRequestContext;

        const isMobileCheck = isMobile({ variant: VariantType.Browser, context });
        return isMobileCheck === 'xs' || isMobileCheck === 'sm';
    };

    private _onIncrement(): void {
        let invokeCallback = false;
        const currQuantity = this.state.currentInput;
        let updatedQuantity: number;


        if (this.props.UOMCollection !== undefined) {
            this.nst++;
            const area = this.toTwoDecimal(this.nst * this.conversion);
            this.converage = area;
            this.setState({ currentInput: area });
            if (invokeCallback && this.props.onChange) {
                this.props.onChange(area);
            }
        } else if (this.props.multipleOfQuantity && this.props.multipleOfQuantity !== 0) {
            if (currQuantity <= this.props.max && (currQuantity + this.props.multipleOfQuantity) <= this.props.max) {
                invokeCallback = true;
                updatedQuantity = currQuantity + this.props.multipleOfQuantity;
            } else {
                invokeCallback = false;
                updatedQuantity = this.props.max;
            }

            if (invokeCallback && this.props.onChange) {
                if (this.props.onChange(updatedQuantity)) {
                    this.setState({ currentInput: updatedQuantity });
                }
            }
        } else {
            if (currQuantity < this.props.max) {
                invokeCallback = true;
                updatedQuantity = currQuantity + 1;
            } else {
                invokeCallback = false;
                updatedQuantity = this.props.max;
            }

            if (invokeCallback && this.props.onChange) {
                if (this.props.onChange(updatedQuantity)) {
                    this.setState({ currentInput: updatedQuantity });
                }
            }
        }
    }

    private _onDecrement(): void {
        let invokeCallback = false;
        const currQuantity = this.state.currentInput;
        let updatedQuantity: number;


        if (this.props.UOMCollection !== undefined) {
            if (this.nst > 1) {
                this.nst--;
                const area = this.toTwoDecimal(this.nst * this.conversion);
                this.converage = area;

                this.setState({ currentInput: area });
                if (invokeCallback && this.props.onChange) {
                    this.props.onChange(area);
                }
            }
        } else if (this.props.multipleOfQuantity && this.props.multipleOfQuantity !== 0) {
            if (currQuantity >= this._minValue && (currQuantity - this.props.multipleOfQuantity) >= this._minValue) {
                invokeCallback = true;
                updatedQuantity = currQuantity - this.props.multipleOfQuantity;
            } else {
                invokeCallback = false;
                updatedQuantity = this._minValue;
            }
            if (invokeCallback && this.props.onChange) {
                if (this.props.onChange(updatedQuantity)) {
                    this.setState({ currentInput: updatedQuantity });
                }
            }
        } else {
            if (currQuantity >= this._minValue) {
                invokeCallback = true;
                updatedQuantity = currQuantity - 1;
            } else {
                invokeCallback = false;
                updatedQuantity = this._minValue;
            }
            if (invokeCallback && this.props.onChange) {
                if (this.props.onChange(updatedQuantity)) {
                    this.setState({ currentInput: updatedQuantity });
                }
            }
        }
    }

    private toTwoDecimal(num: number) {
        return Math.round(num * 100) / 100;
    }

    private _handleCoverageChange(e: React.ChangeEvent<HTMLInputElement>): void {
        let invokeCallback = false;
        const coverage2 = Number.parseInt(e.target.value, 10);
        this.nst = Math.ceil(coverage2 / this.conversion);
        var updatedQuantity = this.toTwoDecimal(this.nst * this.conversion);

        if (updatedQuantity >= this._minValue && updatedQuantity <= this.props.max) {
            invokeCallback = true;
        }
        if (invokeCallback && this.props.onChange) {
            if (this.props.onChange(updatedQuantity)) {
                this.setState({ currentInput: updatedQuantity });
            }
        }
        this.converage = coverage2;
    }

    private _handleNstChange(e: React.ChangeEvent<HTMLInputElement>): void {
        let invokeCallback = false;
        const nst2 = Number.parseInt(e.target.value, 10);
        const area = this.toTwoDecimal(nst2 * this.conversion);
        if (area >= this._minValue && area <= this.props.max) {
            invokeCallback = true;
        }

        if (invokeCallback && this.props.onChange) {
            if (this.props.onChange(area)) {
                this.setState({ currentInput: area });
            }
        }

        this.converage = area;
        this.nst = nst2;
    }

    private _handleChange(e: React.ChangeEvent<HTMLInputElement>): void {
        let currentValue = Number.parseInt(e.target.value, 10);
        if (Number.isNaN(currentValue)) {
            this.setState({ currentInput: currentValue });
            return;
        }
        currentValue = Math.min(this.props.max, Math.max(this._minValue, currentValue));
        this.setState({ currentInput: currentValue });
        debounce(() => {
            // Due of usage debouncer, we may ignore onChange return value
            this.props.onChange && this.props.onChange(this.state.currentInput);

            const inputElement =
                this.inputRef && this.inputRef.current && this.inputRef.current instanceof HTMLInputElement && this.inputRef.current;
            if (inputElement) {
                inputElement.setAttribute('aria-valuenow', this.state.currentInput.toString());
                inputElement.setAttribute('value', this.state.currentInput.toString());
            }
        }, 1000)();
    }

    /**
     * Validates min and updates.
     */
    private readonly _validateMin = (): void => {
        if (Number.isNaN(this.state.currentInput)) {
            const min = Math.min(this._minValue, this.props.multipleOfQuantity ?? this._minValue);
            this.setState({ currentInput: min });
        } else {
            var currentValue = this.state.currentInput;
            if (this.props.multipleOfQuantity && this.props.multipleOfQuantity !== 0) {
                currentValue = Math.round(currentValue / this.props.multipleOfQuantity) * this.props.multipleOfQuantity;
                currentValue = (currentValue < this.props.multipleOfQuantity) ? this.props.multipleOfQuantity : currentValue;
            }
            currentValue = Math.min(this.props.max, Math.max(this._minValue, currentValue));
            if (currentValue !== this.state.currentInput) {
                this.setState({ currentInput: currentValue });
                 if (this.props.onChange) {
                    this.props.onChange(currentValue);
                }
            }
        }
    };
}
